import React, { useState, useEffect } from 'react';
import axios from 'axios';
import UserCheck from './UserCheck';
import Einreise from './Einreise';
import './LiteDashboard.css';

const API_BASE = process.env.REACT_APP_API_BASE;

const LiteDashboard = ({ userData, handleLogout }) => {
    const [activeView, setActiveView] = useState('user-check');
    const [userPermissions, setUserPermissions] = useState([]);
    const [loading, setLoading] = useState(true);

    const isSuperAdmin = userData?.id === process.env.REACT_APP_SUPER_ADMIN_USER;

    const hasPermission = (permissionName) => {
        if (isSuperAdmin) return true;
        if (Array.isArray(userPermissions) && userPermissions.includes(permissionName)) return true;
        if (userData?.permissions && Array.isArray(userData.permissions)) return userData.permissions.includes(permissionName);
        if (userData?.assignedRoles && Array.isArray(userData.assignedRoles)) {
            return userData.assignedRoles.some(role => role.permissions && role.permissions.includes(permissionName));
        }
        if (Array.isArray(userData?.roles)) {
            const requiredRoleId = '1492514375905316940';
            if (userData.roles.includes(requiredRoleId)) return true;
        }
        return false;
    };

    useEffect(() => {
        const loadPermissions = async () => {
            try {
                const token = localStorage.getItem('discord_token');
                const response = await axios.get(`${API_BASE}/api/my-permissions`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUserPermissions(response.data || []);
            } catch (err) {
                console.error('Fehler beim Laden der Berechtigungen:', err);
            } finally {
                setLoading(false);
            }
        };
        loadPermissions();
    }, []);

    const canAccess = () => {
        if (isSuperAdmin) return true;
        if (hasPermission('access_lite_dashboard')) return true;
        return false;
    };

    const navigateTo = (view) => {
        setActiveView(view);
    };

    if (loading) {
        return (
            <div className="app-shell full-screen center">
                <div className="spinner" />
                <p className="muted">Lädt...</p>
            </div>
        );
    }

    if (!canAccess()) {
        return (
            <div className="app-shell full-screen center">
                <div className="widget-card access-denied">
                    <div className="access-denied-icon">🚫</div>
                    <h2>Zugang verweigert</h2>
                    <p>Sie haben keine Berechtigung für dieses Panel.</p>
                    <p className="muted">Kontaktieren Sie einen Administrator.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="app-shell">
            <div className="particles-bg" />
            <aside className="sidebar-container lite-sidebar">
                <div className="sidebar-brand">
                    <svg className="hexagon-logo" viewBox="0 0 100 100" width="32" height="32">
                        <polygon points="50,5 95,25 95,75 50,95 5,75 5,25" fill="none" stroke="#00f0ff" strokeWidth="6" />
                        <polygon points="50,20 80,35 80,65 50,80 20,65 20,35" fill="none" stroke="#d946ef" strokeWidth="4" />
                    </svg>
                    <span className="brand-text">SALERNO City</span>
                </div>

                <nav className="sidebar-nav">
                    <button
                        className={`nav-item single ${activeView === 'user-check' ? 'active' : ''}`}
                        onClick={() => navigateTo('user-check')}
                        style={{ '--item-accent': '#00ffc6' }}
                    >
                        <span className="nav-icon-emoji">🔎</span>
                        <span className="item-name">User Check</span>
                        {activeView === 'user-check' && <div className="active-glow-indicator" />}
                    </button>
                    <button
                        className={`nav-item single ${activeView === 'einreise' ? 'active' : ''}`}
                        onClick={() => navigateTo('einreise')}
                        style={{ '--item-accent': '#00ffc6' }}
                    >
                        <span className="nav-icon-emoji">🛬</span>
                        <span className="item-name">Einreise</span>
                        {activeView === 'einreise' && <div className="active-glow-indicator" />}
                    </button>
                </nav>

                <div className="sidebar-status-container">
                    <div className="status-title">LITE PANEL</div>
                    <div className="sidebar-status-item">
                        <div className="status-labels">
                            <span>Module</span>
                            <span className="status-percentage cyan">2/2</span>
                        </div>
                        <div className="status-progressbar">
                            <div className="status-progress-fill bg-cyan" style={{ width: '100%' }} />
                        </div>
                    </div>
                </div>
            </aside>

            <header className="top-header-panel">
                <div className="header-search">
                    <span className="search-icon">🔍</span>
                    <input type="text" placeholder="Search systems..." readOnly />
                </div>
                <div className="header-actions">
                    <div className="header-divider" />
                    <div className="user-profile-badge">
                        <div className="avatar-wrapper">
                            <img src={userData?.avatarUrl || 'https://cdn.discordapp.com/embed/avatars/0.png'} alt="Avatar" className="user-avatar-img" />
                            <span className="online-badge-dot" />
                        </div>
                        <div className="user-meta-details">
                            <span className="user-display-name">{userData?.nickname || userData?.global_name || 'Benutzer'}</span>
                            <span className={`user-role-label ${isSuperAdmin ? 'admin' : 'member'}`}>
                                {isSuperAdmin ? 'Super Admin' : (userData?.assignedRoles?.[0]?.name || 'Mitglied')}
                            </span>
                        </div>
                    </div>
                    <button className="header-logout-btn" onClick={handleLogout} title="Abmelden">
                        <span>Abmelden</span>
                    </button>
                </div>
            </header>

            <div className="main-content">
                {activeView === 'user-check' && <UserCheck />}
                {activeView === 'einreise' && <Einreise userData={userData} />}
            </div>
        </div>
    );
};

export default LiteDashboard;
